import type { RightDrawerTabType, RightDrawerTableItemType } from './types';
import type { TOwnerScope } from './useOwnerScope';
import type { TStudyRelevance } from './useStudyRelevance';

import { useDebouncedState } from 'hooks/useDebouncedState';
import {
  LIST_MACROS,
  LIST_REPORT_TEMPLATES,
  GET_MACRO,
  GET_REPORT_TEMPLATE,
} from 'modules/Apollo/queries';
import { OWNER_SCOPE, useOwnerScope } from './useOwnerScope';
import { STUDY_RELEVANCE, useStudyRelevance } from './useStudyRelevance';
import { getOwnerScopeFromURL, getStudyRelevanceFromURL } from './utils';

import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SEARCH_DEBOUNCE_MS } from 'config/constants';
import { NetworkStatus, useLazyQuery, useQuery } from '@apollo/client';
import useMountedState from 'react-use/esm/useMountedState';
import { RightDrawerTab } from './types';
import type { SlateContent } from '../types';
import { unfurlMacroPlaceholderStatic } from '../../RichTextEditor/plugins/macroPlaceholder/useMacroPlaceholder';
import { useRecoilState } from 'recoil';
import { rightDrawerFilterState } from './state';
import { WithApolloContext } from 'modules/Apollo/types';
import { usePlugins } from 'domains/reporter/RichTextEditor';
import { useRightDrawerTablePaginationTelemetry } from './useRightDrawerTableSpinnerTelemetry';

export type useRightDrawerTabReturn = {
  ownerScope: TOwnerScope;
  setOwnerScope: (arg1: TOwnerScope) => void;
  studyRelevance: TStudyRelevance;
  setStudyRelevance: (arg1: TStudyRelevance) => void;
  search: string;
  setSearch: (arg1: string) => void;
  onLoadMore: () => void;
  cursor: Nullable<string>;
  items: Array<RightDrawerTableItemType>;
  hasError: boolean;
  isLoading: boolean;
  fetchPreviewContent: (arg1: string) => Promise<Nullable<SlateContent>>;
  order: string;
  setOrder: (arg1: string) => void;
  refetchItems: () => Promise<void>;
  networkStatus: NetworkStatus;
  dataItemsLength: Nullable<number>;
  previousDataItemsLength: Nullable<number>;
};

type useRightDrawerTabProps = WithApolloContext & {
  tab: RightDrawerTabType;
  procedureSmid: string;
  skip?: boolean;
  sortFunction?: (arg1: Array<RightDrawerTableItemType>) => Array<RightDrawerTableItemType>;
  setIsPreviewLoading: (arg1: boolean) => void;
};

export const useRightDrawerTab = ({
  tab,
  procedureSmid,
  skip,
  sortFunction,
  setIsPreviewLoading,
  context,
}: useRightDrawerTabProps): useRightDrawerTabReturn => {
  const pluginsBag = usePlugins();

  const query = useMemo(
    () => (tab === RightDrawerTab.Template ? LIST_REPORT_TEMPLATES : LIST_MACROS),
    [tab]
  );

  const [search, searchDebounced, setSearch] = useDebouncedState('', SEARCH_DEBOUNCE_MS);
  const isMounted = useMountedState();
  const [searchParams, setSearchParams] = useSearchParams();
  searchParams.sort();
  const searchString = searchParams.toString();

  const [filterState, setFilterState] = useRecoilState(rightDrawerFilterState);

  const order =
    tab === RightDrawerTab.Template ? filterState.templateOrder : filterState.macroOrder;

  const setOrder = useCallback(
    (newOrder: string) => {
      setFilterState((prev) => ({
        ...prev,
        [tab === RightDrawerTab.Template ? 'templateOrder' : 'macroOrder']: newOrder,
      }));
    },
    [setFilterState, tab]
  );

  const ownerScope =
    tab === RightDrawerTab.Template ? filterState.templateOwner : filterState.macroOwner;
  const studyRelevance =
    tab === RightDrawerTab.Template ? filterState.templateRelevance : filterState.macroRelevance;

  const setOwnerScope = useCallback(
    (newOwnerScope: TOwnerScope) => {
      setFilterState((prev) => ({
        ...prev,
        [tab === RightDrawerTab.Template ? 'templateOwner' : 'macroOwner']: newOwnerScope,
      }));
    },
    [setFilterState, tab]
  );

  const setStudyRelevance = useCallback(
    (newStudyRelevance: TStudyRelevance) => {
      setFilterState((prev) => ({
        ...prev,
        [tab === RightDrawerTab.Template ? 'templateRelevance' : 'macroRelevance']:
          newStudyRelevance,
      }));
    },
    [setFilterState, tab]
  );

  useEffect(() => {
    const urlOwnerScope = getOwnerScopeFromURL(tab, searchParams);
    const urlStudyRelevance = getStudyRelevanceFromURL(tab, searchParams);

    const hasUrlOwnerFilter = urlOwnerScope !== OWNER_SCOPE.All;
    const hasUrlRelevanceFilter = urlStudyRelevance !== STUDY_RELEVANCE.All;

    // check if url has filters, otherwise keep current persisted state
    setFilterState((prev) => {
      const currentOwner = tab === RightDrawerTab.Template ? prev.templateOwner : prev.macroOwner;
      const currentRelevance =
        tab === RightDrawerTab.Template ? prev.templateRelevance : prev.macroRelevance;

      // update if url has filters and they differ from current state to avoid unnecessary re-renders when the url changes
      const shouldUpdateOwner = hasUrlOwnerFilter && urlOwnerScope !== currentOwner;
      const shouldUpdateRelevance = hasUrlRelevanceFilter && urlStudyRelevance !== currentRelevance;

      if (shouldUpdateOwner || shouldUpdateRelevance) {
        return {
          ...prev,
          [tab === RightDrawerTab.Template ? 'templateOwner' : 'macroOwner']: urlOwnerScope,
          [tab === RightDrawerTab.Template ? 'templateRelevance' : 'macroRelevance']:
            urlStudyRelevance,
        };
      }

      return prev;
    });
  }, [tab, searchParams, setFilterState]);

  useEffect(() => {
    const updatedParams = new URLSearchParams(searchString);

    if (ownerScope === OWNER_SCOPE.All) {
      updatedParams.delete(`${tab}Owner`);
    } else {
      updatedParams.set(`${tab}Owner`, ownerScope);
    }

    if (studyRelevance === STUDY_RELEVANCE.All) {
      updatedParams.delete(`${tab}Relevance`);
    } else {
      updatedParams.set(`${tab}Relevance`, studyRelevance);
    }

    updatedParams.sort();

    if (updatedParams.toString() !== searchString) {
      setSearchParams(updatedParams);
    }
  }, [ownerScope, studyRelevance, searchString, setSearchParams, tab]);

  const owners = useOwnerScope({ scope: ownerScope, context });

  const procedureSmids = useStudyRelevance(studyRelevance, procedureSmid);
  const querySearch = searchDebounced === '' ? undefined : searchDebounced;

  const { data, loading, error, fetchMore, previousData, refetch, networkStatus } = useQuery(
    query,
    {
      variables: {
        owners,
        procedures: procedureSmids,
        search: querySearch,
        order,
      },
      skip,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      context: {
        ...context,
        telemetry: { ...context.telemetry, fetchPolicy: 'cache-and-network' },
      },
    }
  );
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const refetchItems = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const dataItems = data != null ? data[`${tab}s`]?.items : null;
  const previousDataItems = previousData != null ? previousData[`${tab}s`]?.items : null;
  const dataItemsLength = dataItems?.length ?? null;
  const previousDataItemsLength = previousDataItems?.length ?? null;

  const items = useMemo(() => {
    let items: Array<RightDrawerTableItemType> = [];
    if (loading && !isFetchingMore) {
      return [];
    }

    if (!error) {
      const dataItemsForRender = dataItems ?? previousDataItems;

      if (dataItemsForRender != null && dataItemsForRender.length > 0) {
        items = dataItemsForRender?.map((item: any) => {
          return {
            id: item.id || item.smid || '',
            name: item.name,
            owner: item.owner?.id,
            lastUsedDateTime: item.lastUsedDateTime,
          };
        });
      }
    } else {
      return [{ id: '', name: `An error has occurred.` }];
    }

    if (items == null || items?.length === 0) {
      return [{ id: '', name: `No ${tab}s found` }];
    }

    if (sortFunction != null) {
      return sortFunction(items);
    }

    return items;
  }, [loading, isFetchingMore, error, sortFunction, dataItems, previousDataItems, tab]);

  const cursor = useMemo(() => {
    const dataList = data != null ? data[`${tab}s`] : null;
    return dataList?.next;
  }, [tab, data]);

  const trackPagination = useRightDrawerTablePaginationTelemetry({
    activeTab: tab,
    cursor,
    dataItemsLength,
    itemsLength: items.length,
    networkStatus,
    order,
    ownerScope,
    previousDataItemsLength,
    search,
    studyRelevance,
  });

  const onLoadMore = useCallback(() => {
    if (items == null || !isMounted() || cursor == null || loading) return;

    trackPagination(
      fetchMore({
        variables: {
          cursor,
          owners,
          procedures: procedureSmids,
          search: querySearch,
          order,
        },
      })
    );
  }, [
    cursor,
    fetchMore,
    isMounted,
    items,
    loading,
    order,
    owners,
    procedureSmids,
    querySearch,
    trackPagination,
  ]);

  const [loadPreviewContent, { loading: isPreviewLoading }] = useLazyQuery(
    tab === RightDrawerTab.Template ? GET_REPORT_TEMPLATE : GET_MACRO,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  useEffect(() => {
    setIsPreviewLoading(isPreviewLoading);
  }, [isPreviewLoading, setIsPreviewLoading]);

  const fetchPreviewContent = useCallback(
    async (id: string) => {
      const { data } = await loadPreviewContent({
        variables: { id },
        context: {
          ...context,
          telemetry: { ...context.telemetry, fetchPolicy: 'cache-and-network' },
        },
      });
      if (data != null) {
        const dataItem = data[`${tab}`];
        if (dataItem != null) {
          const content = tab === RightDrawerTab.Template ? dataItem.content : dataItem.text;
          const macros = tab === RightDrawerTab.Template ? dataItem.macros : dataItem.nestedMacros;
          return unfurlMacroPlaceholderStatic(
            content,
            macros,
            pluginsBag?.getEditorStateEnhancers() ?? [],
            {
              fragmentMode: tab === RightDrawerTab.Macro,
            }
          );
        }
      }

      return null;
    },
    [loadPreviewContent, context, pluginsBag, tab]
  );

  return {
    ownerScope,
    setOwnerScope,
    studyRelevance,
    setStudyRelevance,
    search,
    setSearch,
    onLoadMore,
    cursor,
    items,
    hasError: error != null,
    isLoading: loading,
    fetchPreviewContent,
    order,
    setOrder,
    refetchItems,
    networkStatus,
    dataItemsLength,
    previousDataItemsLength,
  };
};
